import { useRef } from "react";
import { Camera, Images, Video } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Photo / Video / Library pickers. The first two carry `capture`, so on a
 * phone or iPad they open the camera straight away in that mode; Library
 * opens the gallery for anything already recorded. Desktop browsers ignore
 * `capture` and show a file dialog for all three.
 */
export function CaptureButtons({
  onFiles,
  disabled,
  busyLabel,
  labels,
  size = "sm",
}: {
  onFiles: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
  /** Replaces the buttons while an upload runs. */
  busyLabel?: string | null;
  labels?: { photo?: string; video?: string; library?: string };
  size?: "sm" | "xs";
}) {
  const photo = useRef<HTMLInputElement>(null);
  const video = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, 10);
    e.target.value = "";
    if (files.length) void onFiles(files);
  };

  const btn = cn(
    "inline-flex items-center gap-1 rounded-full border uppercase tracking-[0.12em] transition disabled:opacity-50",
    size === "sm" ? "px-3 py-1 text-[0.62rem]" : "px-2.5 py-1 text-[0.58rem] font-semibold",
  );
  const gold = cn(btn, "border-gold/50 bg-gold/10 text-goldbright hover:bg-gold/20");
  const plain = cn(btn, "border-white/15 text-ice/70 hover:text-paper");

  if (busyLabel) {
    return <span className={cn(gold, "cursor-default")}>{busyLabel}</span>;
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <input ref={photo} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
      <input ref={video} type="file" accept="video/*" capture="environment" className="hidden" onChange={pick} />
      <input ref={library} type="file" accept="image/*,video/*" multiple className="hidden" onChange={pick} />
      <button type="button" className={gold} disabled={disabled} onClick={() => photo.current?.click()}>
        <Camera className="h-3 w-3" /> {labels?.photo ?? "Photo"}
      </button>
      <button type="button" className={gold} disabled={disabled} onClick={() => video.current?.click()}>
        <Video className="h-3 w-3" /> {labels?.video ?? "Video"}
      </button>
      <button type="button" className={plain} disabled={disabled} onClick={() => library.current?.click()}>
        <Images className="h-3 w-3" /> {labels?.library ?? "Library"}
      </button>
    </span>
  );
}
